/**
 * Board painter for `board-grid` levels (design §17) — a clickable W×H grid with a tile
 * palette: select a tile, click/drag to paint, right-click or the eraser to clear. Cells
 * are sparse in the data; the JSON details block stays the escape hatch (same undoable
 * dataText channel as the other editors).
 */
import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { Plus, Eraser } from 'lucide-react';
import type { BoardGridLevel } from '@volley/crucible-levels';

/** Deterministic tile colour from its palette index (readable on the dark theme). */
export function tileColor(index: number): string {
  const hue = (index * 137.508) % 360; // golden angle — well-spread hues
  return `hsl(${hue.toFixed(0)} 62% 52%)`;
}

/** Set/clear one cell in the sparse list (tile null = erase). */
export function paintCell(
  cells: BoardGridLevel['cells'],
  x: number,
  y: number,
  tile: string | null,
): BoardGridLevel['cells'] {
  const rest = cells.filter((c) => c.x !== x || c.y !== y);
  return tile === null ? rest : [...rest, { x, y, tile }];
}

export function BoardGridEditor({
  data,
  onChange,
}: {
  data: BoardGridLevel;
  onChange: (next: BoardGridLevel) => void;
}): ReactElement {
  const [brush, setBrush] = useState<string | null>(data.palette[0] ?? null);
  const [newTile, setNewTile] = useState('');
  const byCell = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of data.cells) m.set(`${c.x},${c.y}`, c.tile);
    return m;
  }, [data.cells]);
  const colorOf = useMemo(() => {
    const m = new Map<string, string>();
    data.palette.forEach((t, i) => m.set(t, tileColor(i)));
    return m;
  }, [data.palette]);

  const paint = (x: number, y: number): void =>
    onChange({ ...data, cells: paintCell(data.cells, x, y, brush) });

  const addTile = (): void => {
    const t = newTile.trim();
    if (!t || data.palette.includes(t)) return;
    onChange({ ...data, palette: [...data.palette, t] });
    setBrush(t);
    setNewTile('');
  };

  const setSize = (key: 'width' | 'height', v: number): void => {
    const size = Math.max(1, Math.min(64, Math.round(v) || 1));
    // Drop cells that fall outside the new bounds.
    const cells = data.cells.filter((c) => (key === 'width' ? c.x < size : c.y < size));
    onChange({ ...data, [key]: size, cells });
  };

  return (
    <div
      className="space-y-2 rounded-lg border border-crucible-border bg-crucible-bg p-3"
      data-testid="board-grid-editor"
    >
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-medium text-crucible-fg">Board</span>
        <label className="flex items-center gap-1 text-crucible-muted">
          W
          <input
            type="number"
            min={1}
            max={64}
            value={data.width}
            onChange={(e) => setSize('width', Number(e.target.value))}
            className="w-14 rounded border border-crucible-border bg-crucible-bg-elevated px-1.5 py-0.5 text-right text-xs text-crucible-fg"
          />
        </label>
        <label className="flex items-center gap-1 text-crucible-muted">
          H
          <input
            type="number"
            min={1}
            max={64}
            value={data.height}
            onChange={(e) => setSize('height', Number(e.target.value))}
            className="w-14 rounded border border-crucible-border bg-crucible-bg-elevated px-1.5 py-0.5 text-right text-xs text-crucible-fg"
          />
        </label>
        <span className="text-crucible-muted">
          {data.cells.length} cell{data.cells.length === 1 ? '' : 's'} painted
        </span>
      </div>

      {/* Palette: pick a brush, add tiles (ideally exact Content item names). */}
      <div className="flex flex-wrap items-center gap-1.5">
        {data.palette.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setBrush(t)}
            data-testid={`board-tile-${t}`}
            className={`flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] ${
              brush === t
                ? 'border-crucible-accent text-crucible-fg'
                : 'border-crucible-border text-crucible-muted hover:text-crucible-fg'
            }`}
          >
            <span
              className="inline-block size-2.5 rounded-sm"
              style={{ background: colorOf.get(t) }}
            />
            {t}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setBrush(null)}
          title="Eraser — click cells to clear them"
          className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${
            brush === null
              ? 'border-crucible-accent text-crucible-fg'
              : 'border-crucible-border text-crucible-muted hover:text-crucible-fg'
          }`}
        >
          <Eraser size={11} /> erase
        </button>
        <span className="flex items-center gap-1">
          <input
            value={newTile}
            onChange={(e) => setNewTile(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') addTile();
            }}
            placeholder="new tile (Content name)"
            data-testid="board-new-tile"
            className="w-40 rounded border border-crucible-border bg-transparent px-2 py-0.5 text-[11px] text-crucible-fg outline-none focus:border-crucible-accent"
          />
          <button
            type="button"
            onClick={addTile}
            disabled={!newTile.trim()}
            aria-label="Add tile"
            className="rounded border border-dashed border-crucible-border p-1 text-crucible-muted hover:text-crucible-fg disabled:opacity-40"
          >
            <Plus size={11} />
          </button>
        </span>
      </div>

      {/* The grid. Click paints the brush; drag with the button held to paint a stroke. */}
      <div className="overflow-auto">
        <div
          className="grid w-max select-none gap-px rounded border border-crucible-border bg-crucible-border/60 p-px"
          style={{ gridTemplateColumns: `repeat(${data.width}, 22px)` }}
          onContextMenu={(e) => e.preventDefault()}
        >
          {Array.from({ length: data.height }, (_, y) =>
            Array.from({ length: data.width }, (_, x) => {
              const tile = byCell.get(`${x},${y}`);
              return (
                <button
                  key={`${x},${y}`}
                  type="button"
                  title={tile ? `${tile} (${x},${y})` : `(${x},${y})`}
                  data-testid={`board-cell-${x}-${y}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onChange({
                      ...data,
                      cells: paintCell(data.cells, x, y, e.button === 2 ? null : brush),
                    });
                  }}
                  onMouseEnter={(e) => {
                    if (e.buttons === 1) paint(x, y);
                  }}
                  className="size-[22px] bg-crucible-bg hover:brightness-125"
                  style={tile ? { background: colorOf.get(tile) ?? '#888' } : undefined}
                />
              );
            }),
          )}
        </div>
      </div>
      <p className="text-[11px] text-crucible-muted">
        Click/drag to paint the selected tile; right-click (or the eraser) clears. Name tiles after
        Content items so cross-references stay navigable.
      </p>
    </div>
  );
}
